// src/components/app-shell/AppShellInitializer.ts
import { firstValueFrom, forkJoin, Subscription } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { apiService } from '../../services/api-service'
import { chatStore } from '../../store'
import { AppShell } from './AppShell'
import { Character } from '../../store'

export class AppShellInitializer {
    private _unsubscribe: Subscription | undefined
    private _resizeObserver: ResizeObserver | null = null
    private _sessionCheckInterval: number | null = null

    constructor(private appShell: AppShell) {}

    async initialize() {
        await this.initializeApplication()
        this.setupResizeObserver()
        this.setupStoreSubscription()
    }

    cleanup() {
        if (this._unsubscribe) {
            this._unsubscribe.unsubscribe()
        }
        if (this._resizeObserver) {
            this._resizeObserver.disconnect()
        }
        if (this._sessionCheckInterval !== null) {
            window.clearInterval(this._sessionCheckInterval)
        }
    }

    private async initializeApplication() {
        console.log('Starting application initialization')
        try {
            // 1. Fetch initial data
            console.log('Fetching initial data...')
            const { conversations, characters } = await firstValueFrom(
                apiService.login().pipe(
                    switchMap(() =>
                        forkJoin({
                            conversations: apiService.getConversations(),
                            characters: apiService.getCharacters(),
                        }),
                    ),
                ),
            )
            console.log('Fetched data:', {
                conversationCount: conversations.length,
                characterCount: characters.length,
            })

            // 2. Initialize store with chat list
            console.log('Initializing store...')
            chatStore.setCharacters(characters)
            this.appShell._characters = characters

            // Check for Sophia's history first
            console.log('Checking for Sophia history...')
            const sophiaHistory = await firstValueFrom(apiService.getCharacterHistory('sophia'))
            console.log('Sophia history:', sophiaHistory.length, 'messages')

            if (sophiaHistory.length > 0) {
                // Sophia chat exists, initialize it with history
                console.log('Initializing existing Sophia chat')
                const details = await firstValueFrom(apiService.getCharacterDetails('sophia'))
                chatStore.setActiveCharacter('sophia')
                chatStore.initializeChat('sophia', details.name)
                chatStore.setActiveChat('sophia')
                this.addMessagesToStore(sophiaHistory, 'sophia')
            } else if (conversations.length === 0) {
                // No Sophia chat and no conversations, create new Sophia chat
                console.log('No existing chats, initializing new Sophia chat')
                await this.initializeSophiaChat(characters)
            }

            // Initialize regular conversations if any
            if (conversations.length > 0) {
                console.log('Initializing regular conversations')
                conversations.forEach(conversation => {
                    console.log('Initializing chat:', conversation.id)
                    const isCharacterChat = characters.some(
                        char => char.id === conversation.philosopherId,
                    )
                    chatStore.initializeChat(
                        conversation.id,
                        conversation.philosopherName,
                        '',
                        conversation.philosopherId,
                    )

                    if (isCharacterChat) {
                        chatStore.setActiveCharacter(conversation.philosopherId)
                    }
                })

                // Load histories for regular conversations
                await this.loadMessageHistories(conversations, characters)

                // Set first conversation as active if Sophia chat doesn't exist
                if (sophiaHistory.length === 0) {
                    chatStore.setActiveChat(conversations[0].id)
                }
            }
        } catch (error) {
            console.error('Failed to initialize application:', error)
        }
    }

    private addMessagesToStore(messages: any[], chatId: string) {
        console.log('Adding messages to store for chat:', chatId)
        chatStore.clearMessages(chatId)
        messages.forEach(msg => {
            if (msg.role === 'user') {
                chatStore.addUserMessage(msg.content, chatId, true)
            } else {
                chatStore.addBotMessage(msg.content, chatId, true)
            }
        })
    }

    private async initializeSophiaChat(characters: Character[]) {
        console.log('Initializing Sophia chat')
        const sophia = characters.find(char => char.id === 'sophia')
        if (!sophia) {
            console.error('Sophia character not found')
            this.handleSophiaNotFound()
            return
        }

        try {
            const details = await firstValueFrom(apiService.getCharacterDetails('sophia'))
            console.log('Got Sophia details:', details)

            chatStore.setActiveCharacter('sophia')
            const chatId = 'sophia'
            chatStore.initializeChat(chatId, details.name)

            if (details.initialState) {
                chatStore.updateCharacterState('sophia', {
                    currentState: details.initialState,
                    progress: 0,
                })
            }

            chatStore.setActiveChat(chatId)
            console.log('Initialized Sophia chat')

            // Only send __init__ for new Sophia chat
            console.log('Sending initial message to Sophia')
            const response = await firstValueFrom(
                apiService.sendCharacterMessage('sophia', '__init__'),
            )

            if (response) {
                console.log('Received Sophia response:', response)
                chatStore.addBotMessage(response.message, 'sophia')
                if (response.state) {
                    chatStore.updateCharacterState('sophia', response.state)
                }
            }
        } catch (error) {
            console.error('Failed to initialize Sophia:', error)
            this.handleSophiaError()
        }
    }

    private async loadMessageHistories(conversations: any[], characters: Character[]) {
        if (conversations.length === 0) return

        // Load all messages first before proceeding
        for (const conversation of conversations) {
            const isCharacterChat = characters.some(char => char.id === conversation.philosopherId)

            try {
                const messages = await firstValueFrom(
                    isCharacterChat
                        ? apiService.getCharacterHistory(conversation.philosopherId)
                        : apiService.getMessages(conversation.id),
                )

                // Clear and add messages to store
                chatStore.clearMessages(conversation.id)
                messages.forEach(msg => {
                    if (msg.role === 'user') {
                        chatStore.addUserMessage(msg.content, conversation.id, true)
                    } else {
                        chatStore.addBotMessage(msg.content, conversation.id, true)
                    }
                })

                // If it's a character chat, ensure state is properly set
                if (isCharacterChat && messages.length > 0) {
                    chatStore.setActiveCharacter(conversation.philosopherId)
                }
            } catch (error) {
                console.error(`Failed to load messages for conversation ${conversation.id}:`, error)
            }
        }
    }

    private handleSophiaNotFound() {
        console.error('Sophia character not found')
        chatStore.setError('Could not find Sophia character')
        const chatId = 'sophia'
        chatStore.initializeChat(chatId, 'Sophia')
        chatStore.addBotMessage(
            'Error: Sophia character not found. The system might be misconfigured or the service might be unavailable.',
            chatId,
        )
    }

    private handleSophiaError() {
        chatStore.setError('Failed to start conversation with Sophia. Please try again.')
        chatStore.addBotMessage(
            'Error: Failed to establish connection with Sophia. The service might be unavailable or experiencing technical difficulties.',
            'sophia',
        )
    }

    private setupResizeObserver() {
        this._resizeObserver = new ResizeObserver(() => {
            const newIsMobile = window.innerWidth < 60 * 16 // 60rem
            if (newIsMobile !== this.appShell._isMobile) {
                this.appShell._isMobile = newIsMobile
                if (!newIsMobile) {
                    this.appShell._showChatList = true
                }
            }
        })

        this._resizeObserver.observe(document.documentElement)
        this.appShell._isMobile = window.innerWidth < 60 * 16
    }

    private setupStoreSubscription() {
        this._unsubscribe = chatStore.subscribe(state => {
            this.appShell._chats = Object.values(state.chats).map(chat => ({
                id: chat.id,
                name: chat.name,
                lastMessage: chat.lastMessage,
                timestamp: chat.timestamp,
                personalityId: chat.personalityId || '',
            }))

            if (state.activeChatId && state.activeChatId !== this.appShell._selectedChatId) {
                this.appShell._selectedChatId = state.activeChatId
            }

            if (state.activeChatId && state.chats[state.activeChatId]) {
                this.appShell._messages = state.chats[state.activeChatId].messages
            } else {
                this.appShell._messages = []
            }
        })
    }
}
